import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { useLocalization } from 'localization';
import { UsersApi } from 'api';
import routes from 'app.routes';
import QUERIES from 'modules/common/queries';
import notifyHandler from 'utils/notifyHandler';

interface IFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  newPassword: string;
  repeatPassword: string;
}

const initialValues: IFormValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  newPassword: '',
  repeatPassword: '',
};

export const useAcceptInvitation = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const {
    data: foundInvite,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [QUERIES.inviteById, id],
    queryFn: () => UsersApi.findInviteById(id as string),
    enabled: !!id,
  });

  const { mutateAsync: acceptInvite, isPending: isAccepting } = useMutation({
    mutationFn: UsersApi.acceptInvite,
    onSuccess: result => {
      if (result.success) {
        notifyHandler({ type: 'success', content: t('users.pages.acceptInvite.success') });
        navigate(routes.login());
        return;
      }
      notifyHandler({ type: 'error', content: t('users.pages.acceptInvite.failed') });
    },
  });

  const formSchema = yup.object().shape({
    firstName: yup.string().required(t('validation.mixed.required')),
    lastName: yup.string().required(t('validation.mixed.required')),
    phoneNumber: yup
      .string()
      .required(t('validation.mixed.required'))
      .test('phonePossible', t('validation.mixed.default'), val => {
        if (!val) return true;
        const p = parsePhoneNumberFromString(val);
        return !!p && p.isPossible() && p.isValid();
      }),
    newPassword: yup.string().required(t('validation.mixed.required')).min(8).password(),
    repeatPassword: yup
      .string()
      .required(t('validation.mixed.required'))
      .oneOf([yup.ref('newPassword')], t('validation.custom.matchesField', { otherField: t('common.terms.password') })),
  });

  const form = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const { watch, handleSubmit, getValues } = form;

  const hasNameField = watch('firstName') || watch('lastName');

  const welcomeMessage =
    hasNameField && `,${watch('firstName') && ` ${watch('firstName')}`}${watch('lastName') && ` ${watch('lastName')}`}`;

  const acceptInvitation = handleSubmit(() => {
    const values = getValues();
    acceptInvite({
      id: id as string,
      firstName: values.firstName,
      lastName: values.lastName,
      mobilePhone: values.phoneNumber,
      password: values.newPassword as string,
    });
  });

  return {
    form,
    ui: {
      welcomeMessage: t('common.terms.welcome') + welcomeMessage,
    },
    action: {
      acceptInvitation,
    },
    apiState: {
      isLoading,
      isError,
      foundInvite,
      isAccepting,
    },
  };
};
